import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import { useLanguage } from '../../context/LanguageContext';
import { MARKETPLACE_ICONS } from '../../assets/marketplaceIcons';
import api from '../../services/api';

const AddStoreModal = ({ show, onHide, onStoreAdded }) => {
  const { t } = useLanguage();
  const [selectedStore, setSelectedStore] = useState('');
  const [storeName, setStoreName] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const marketplaces = [
    {
      id: 'wildberries',
      name: 'Wildberries',
      icon: MARKETPLACE_ICONS.wildberries,
      color: '#CB11AB',
      active: true,
    },
    {
      id: 'ozon',
      name: 'Ozon',
      icon: MARKETPLACE_ICONS.ozon,
      color: '#005BFF',
      active: false,
    },
    {
      id: 'yandex_market',
      name: t('addStore.yandexMarket'),
      icon: MARKETPLACE_ICONS.yandex_market,
      color: '#FF5722',
      active: false,
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStore || !apiKey) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.addStore({
        store_type: selectedStore,
        api_key: apiKey,
        name: storeName || undefined,
      });

      setSuccess(t('addStore.success'));
      setApiKey('');
      setStoreName('');
      setSelectedStore('');
      
      if (onStoreAdded) {
        onStoreAdded(response.data);
      }

      setTimeout(() => {
        onHide();
        setSuccess('');
      }, 1500);
    } catch (err) {
      const errorText = err.data?.errorText || err.data?.api_key;
      if (typeof errorText === 'string') {
        setError(errorText);
      } else if (Array.isArray(errorText)) {
        setError(errorText[0]);
      } else if (typeof errorText === 'object') {
        const messages = Object.values(errorText).flat();
        setError(messages.join(' '));
      } else {
        setError(t('addStore.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedStore('');
    setStoreName('');
    setApiKey('');
    setError('');
    setSuccess('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bold">{t('addStore.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2">
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Form onSubmit={handleSubmit}>
          {/* Marketplace Selection */}
          <Form.Group className="mb-4">
            <Form.Label className="fw-semibold mb-3">
              {t('addStore.selectMarketplace')}
            </Form.Label>
            <Row className="g-3 justify-content-center">
              {marketplaces.map((mp) => (
                <Col key={mp.id} xs={6} sm={4}>
                  <div
                    className={`marketplace-card text-center p-3 rounded-3 h-100 ${
                      selectedStore === mp.id ? 'selected' : ''
                    } ${!mp.active ? 'disabled' : ''}`}
                    onClick={() => mp.active && setSelectedStore(mp.id)}
                    style={{
                      border: selectedStore === mp.id
                        ? `2px solid ${mp.color}`
                        : '2px solid var(--border-color)',
                      opacity: mp.active ? 1 : 0.5,
                    }}
                  >
                    <div className="d-flex justify-content-center mb-2">
                      <img
                        src={mp.icon}
                        alt={mp.name}
                        className="marketplace-logo"
                        style={{
                          width: '64px',
                          height: '64px',
                          borderRadius: '12px',
                          objectFit: 'contain',
                        }}
                      />
                    </div>
                    <div className="fw-semibold">{mp.name}</div>
                    {!mp.active && (
                      <small className="text-secondary d-block mt-1">
                        {t('addStore.comingSoon')}
                      </small>
                    )}
                  </div>
                </Col>
              ))}
            </Row>
          </Form.Group>

          {/* Store Name and API Key Input */}
          {selectedStore && (
            <>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">
                  {t('addStore.storeName')}
                </Form.Label>
                <Form.Control
                  type="text"
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                  placeholder={t('addStore.storeNamePlaceholder')}
                />
                <Form.Text className="form-hint">
                  {t('addStore.storeNameHint')}
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">
                  {t('addStore.enterApiKey')}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={t('addStore.apiKeyPlaceholder')}
                  className="font-monospace"
                  style={{ fontSize: '0.85rem' }}
                />
              </Form.Group>
            </>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0">
        <Button variant="outline-secondary" onClick={handleClose}>
          {t('addStore.cancel')}
        </Button>
        <Button
          className="btn-orange"
          onClick={handleSubmit}
          disabled={!selectedStore || !apiKey || loading}
        >
          {loading ? t('addStore.adding') : t('addStore.add')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddStoreModal;
